const qtySelectCombo = {
  combos: [] as HTMLElement[],
  isBound: false,

  init() {
    this.combos = Array.from(
      document.querySelectorAll('[data-qty-select-combo]')
    ) as HTMLElement[];

    this.bindCombos();
    this.bindOutsideClose();
  },

  bindCombos() {
    this.combos.forEach((combo) => {
      if (combo.dataset.qtyInit === '1') return;
      combo.dataset.qtyInit = '1';

      const form = combo.closest('form') as HTMLFormElement | null;
      if (!form) return;

      const select = combo.querySelector('.qty-select-combo__select') as HTMLSelectElement | null;
      const input = combo.querySelector('.qty-select-combo__input') as HTMLInputElement | null;
      const qtyField = form.querySelector('.js-neto-qty') as HTMLInputElement | null;

      if (!select || !input || !qtyField) return;

      const min = this.toInt(qtyField.getAttribute('min')) ?? 1;
      const max = this.toInt(qtyField.getAttribute('max')) ?? 9999;

      const clamp = (n: number) => Math.min(Math.max(n, min), max);

      const setQty = (n: number) => {
        const value = clamp(n);
        qtyField.value = String(value);
        qtyField.dispatchEvent(new Event('input', { bubbles: true }));
        qtyField.dispatchEvent(new Event('change', { bubbles: true }));
        return value;
      };

      const getQty = () => clamp(this.toInt(qtyField.value) ?? min);

      const getCustomOption = () =>
        select.querySelector('option[value="custom"]') as HTMLOptionElement | null;

      const hasOptionValue = (value: string) => {
        return !!select.querySelector(`option[value="${value}"]`);
      };

      const setCustomOptionLabel = (label?: string) => {
        const customOption = getCustomOption();
        if (!customOption) return;
        customOption.textContent = label || 'Custom amount';
      };

      const syncSelectFromQty = () => {
        const qty = getQty();
        const qtyString = String(qty);

        if (hasOptionValue(qtyString)) {
          select.value = qtyString;
          setCustomOptionLabel();
        } else if (hasOptionValue('custom')) {
          setCustomOptionLabel(qtyString);
          select.value = 'custom';
        }
      };

      const showCustom = () => {
        combo.classList.add('is-custom', 'no-chev');
        input.hidden = false;
        input.disabled = false;
        input.min = String(min);
        input.max = String(max);
        input.value = String(getQty());
      };

      const hideCustom = () => {
        combo.classList.remove('is-custom', 'no-chev');
        input.hidden = true;
        input.disabled = true;
      };

      const closeCustomAndRestore = () => {
        hideCustom();
        syncSelectFromQty();
      };

      qtyField.style.display = 'none';

      // init
      setQty(getQty());
      syncSelectFromQty();
      hideCustom();

      select.addEventListener('change', () => {
        if (select.value === 'custom') {
          showCustom();
          return;
        }

        const selectedQty = this.toInt(select.value);
        if (selectedQty == null) return;

        setQty(selectedQty);
        hideCustom();
        setCustomOptionLabel();
      });

      input.addEventListener('input', () => {
        const typedQty = this.toInt(input.value);
        if (typedQty == null) return;

        const nextQty = setQty(typedQty);

        if (hasOptionValue(String(nextQty))) {
          select.value = String(nextQty);
          setCustomOptionLabel();
        } else if (hasOptionValue('custom')) {
          setCustomOptionLabel(String(nextQty));
          select.value = 'custom';
        }
      });

      input.addEventListener('blur', () => {
        const typedQty = this.toInt(input.value);

        if (typedQty == null) {
          closeCustomAndRestore();
          return;
        }

        const nextQty = setQty(typedQty);

        if (hasOptionValue(String(nextQty))) {
          select.value = String(nextQty);
          hideCustom();
          setCustomOptionLabel();
        } else if (hasOptionValue('custom')) {
          setCustomOptionLabel(String(nextQty));
          select.value = 'custom';
          input.value = String(nextQty);
        } else {
          hideCustom();
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          input.blur();
        }
      });

      (
        combo as HTMLElement & {
          __closeCustomAndRestore?: () => void;
        }
      ).__closeCustomAndRestore = closeCustomAndRestore;
    });
  },

  bindOutsideClose() {
    if (this.isBound) return;
    this.isBound = true;

    document.addEventListener(
      'pointerdown',
      (e) => {
        const target = e.target as Node | null;
        if (!target) return;

        this.combos.forEach((combo) => {
          if (combo.contains(target)) return;

          const el = combo as HTMLElement & {
            __closeCustomAndRestore?: () => void;
          };

          el.__closeCustomAndRestore?.();
        });
      },
      { capture: true }
    );
  },

  toInt(value: string | null | undefined) {
    if (value == null || String(value).trim() === '') return null;
    const n = parseInt(String(value), 10);
    return Number.isNaN(n) ? null : n;
  }
};

export default qtySelectCombo;